import {
  isImageAudioFamily,
  OUTDOOR_DEFAULT_SELFCHECK_DOCUMENT,
  OutdoorType,
  VideoPlacement
} from 'core/creative/Creative';
import _ from 'lodash';
import i18n from 'i18n';
import { NativeMediaSummary } from './NativeMediaSummary';
import { VideoMediaSummary } from './VideoMediaSummary';
import { CreativeFormBasicData } from '../FormContent/FormContentModel';
import { ImageMediaSummary } from './ImageMediaSummary';
import { PPSMediaSummary } from './PPSMediaSummary';
import { OutdoorMediaSummary } from './OutdoorMediaSummary';
import {
  CloudStorageManager,
  DefaultCloudStorageManager
} from 'core/cloudStorage/CloudStorageManager';
import { ComboMediaSummary } from './ComboMediaSummary';
import React from 'react';
import { ImageMultipleMediaSummary } from './ImageMultipleMediaSummary';
import {
  DefaultCreativeManager,
  CreativeManager
} from 'core/creative/CreativeManager';
import { AdLogoType } from 'core/adLogo/AdLogo';
import {
  CustomLayoutManager,
  DefaultCustomLayoutManager
} from 'core/customLayout/CustomLayoutManager';
import { CustomLayoutMediaSummary } from './CustomLayoutMediaSummary';
import { MediaSummaryData } from 'components/FlowSummarySection/FlowSummarySection';
import { Currency, LocaleMetaStore } from 'core';
import {
  LineMessageFormData,
  LineMessageType,
  LineUserLanguage
} from 'core/advertiser/LineBeacon';
import { BeaconType } from 'core/advertiser/BeaconConnection';
import { TenMaxBeaconMessageFormData } from 'core/advertiser/TenmaxBeacon';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import {
  formatEpaperDuration,
  getOutdoorTypeLabel,
  usesEpaperDurationFormat
} from 'core/channelGroup/EpaperChannelGroup';

export interface CreativeSummaryModel {
  getCreativeBasicSummaryData: (
    getUploadedFileData?: (file: File) => Promise<any>,
    addUploadedFilesData?: (file: File, data: any) => Promise<void>
  ) => Promise<any>;
  getMediaSummaryComponent: () => React.FC<{ mediaSummary: MediaSummaryData }>;
  getMediaSummary: () => MediaSummaryData;
  getBeaconSummary: () =>
    | {
        frequencyCap?: string;
        language?: string;
        beaconConnection: string;
        lineMessage?: LineMessageFormData[];
        tenMaxBeaconMessage?: TenMaxBeaconMessageFormData;
      }
    | undefined;
  getBasicSubmitData: () => Promise<FormData>;
  getJsonSubmitData: () => Promise<Record<string, any>>;
  getSubmitAlertMessage: () => string | undefined;
}

const getMediaSummaryData = (image) => {
  const file = _.get(image, 'file');
  const url = _.get(image, 'url');
  if (file || url) {
    return {
      file,
      url
    };
  }
};

abstract class DefaultCreativeSummaryModel implements CreativeSummaryModel {
  constructor(
    protected creativeBasicFormData: CreativeFormBasicData,
    protected cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager(),
    protected creativeManager: CreativeManager = new DefaultCreativeManager()
  ) {}

  getI18nOfValueByKey(key, value) {
    switch (key) {
      case 'tenmaxCategory':
        return i18n.t(`tenmaxCategory.labels.${value}`);
      case 'creativeType':
        return this.creativeManager.getCreativeTypeSetting(value).name;
      case 'enableNativeBanner':
        return i18n.t(`common.labels.${value ? 'yes' : 'no'}`);
      default:
        return value;
    }
  }

  transferObjectToSummaryData(data, keyPrefix = '') {
    if (!data) {
      return [];
    }
    const keysPriority = [
      'agencyId',
      'advertiserId',
      'creativeId',
      'name',
      'creativeType',
      'tenmaxCategory',
      'bannerUrl',
      'landingPageUrl'
    ];
    const keys = Object.keys(data).sort((a, b) => {
      const indexA = keysPriority.indexOf(a);
      const indexB = keysPriority.indexOf(b);
      if (indexA === -1 && indexB === -1) {
        return 0;
      } else if (indexA === -1) {
        return 1;
      } else if (indexB === -1) {
        return -1;
      }
      return indexA - indexB;
    });
    return _.flattenDeep(
      keys.map((key) => {
        const currentKey = _.camelCase(`${keyPrefix}-${key}`);
        if (typeof data[key] === 'object' && !React.isValidElement(data[key])) {
          return this.transferObjectToSummaryData(data[key], currentKey);
        }
        const i18nKey = `creativeSetupFlow.labels.${currentKey}`;
        return {
          label: i18n.exists(i18nKey)
            ? i18n.t(`creativeSetupFlow.labels.${currentKey}`)
            : currentKey,
          value: this.getI18nOfValueByKey(currentKey, data[key])
        };
      })
    );
  }

  async getCreativeBasicSummaryData() {
    const omitProps = [
      'advertiserId',
      'medias',
      'typeProperties',
      'bannerExtra',
      'nativeBanner',
      'bannerImageUrl',
      'ppsLayoutId',
      'adLogo'
    ];
    const basic = _.omitBy(
      _.omit(this.creativeBasicFormData, omitProps),
      _.isUndefined
    );
    return _.uniqBy(
      _.compact([
        ...this.transferObjectToSummaryData(basic),
        ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
      ]),
      'label'
    );
  }

  setBasicFormData(formData, omitKeys: string[]) {
    const basic = _.omit(this.creativeBasicFormData, [...omitKeys, 'adLogo']);
    for (const key in basic) {
      if (basic[key] !== undefined && basic[key] !== null) {
        formData.append(`creative.${key}`, basic[key]);
      }
    }
    const adLogo = _.get(this.creativeBasicFormData, 'adLogo');
    if (adLogo) {
      const adLogoData = this.getAdLogoData(adLogo);
      Object.keys(adLogoData).forEach((key) => {
        formData.append(`creative.adLogo[${key}]`, adLogoData[key]);
      });
    }
  }

  getSubmitAlertMessage(): string | undefined {
    return undefined;
  }

  abstract getMediaSummaryComponent();
  abstract getTypePropertiesSummary();
  abstract getMediaSummary();
  abstract getBasicSubmitData(): Promise<FormData>;

  getBeaconSummary():
    | {
        frequencyCap?: string;
        language?: string;
        beaconConnection: string;
        lineMessage?: LineMessageFormData[];
        tenMaxBeaconMessage?: TenMaxBeaconMessageFormData;
      }
    | undefined {
    return undefined;
  }

  async getJsonSubmitData() {
    const formData = await this.getBasicSubmitData();

    const result: Record<string, any> = {};
    formData.forEach((value, key) => {
      const parsedKey = key.replace(/\[(\d+)\]/g, '.$1'); // 將 key[0] 轉換成 key.0
      _.set(result, parsedKey, value);
    });

    return result.creative;
  }

  getAdLogoData(adLogoData) {
    if (adLogoData.type === AdLogoType.CUSTOM) {
      return _.omitBy(
        {
          type: adLogoData.type,
          link: adLogoData.link,
          imgUrl: adLogoData.image.url,
          imgBase64: adLogoData.image.imgBase64
        },
        _.isNil
      );
    }

    return {
      type: adLogoData.type
    };
  }
}

export class NativeSummaryModel extends DefaultCreativeSummaryModel {
  constructor(
    private type: string,
    creativeBasicFormData: CreativeFormBasicData,
    public nativeToBannertemplates: {
      [size: string]: { templatePath?: string; htmlContent?: string };
    },
    cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager);
  }

  getMediaSummaryComponent(): any {
    return NativeMediaSummary;
  }

  getTypePropertiesSummary(): any {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    return {
      title: typeProperties.title,
      desc: typeProperties.desc,
      sponsor: typeProperties.sponsor,
      sponsorLink: typeProperties.sponsorLink
    };
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    const summaryData = {
      nativeToBannertemplates: this.nativeToBannertemplates,
      typeProperties: this.creativeBasicFormData.typeProperties,
      bannerUrl: this.creativeBasicFormData.bannerUrl,
      enableNativeBanner: _.get(
        this.creativeBasicFormData,
        'enableNativeBanner',
        false
      ),
      medias: {}
    };
    if (!_.isEmpty(medias)) {
      Object.keys(medias).forEach((key) => {
        const data = getMediaSummaryData(medias[key]);
        if (data) {
          summaryData.medias[key] = data;
        }
      });
    }
    return summaryData;
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'creativeId',
      'typeProperties',
      'medias',
      'nativeBanner'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    const typeProperties = _.get(
      this.creativeBasicFormData,
      'typeProperties',
      {}
    );
    Object.keys(typeProperties).forEach((key) => {
      formData.append(`creative.typeProperties[${key}]`, typeProperties[key]);
    });
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    Object.keys(medias).forEach((key) => {
      if (medias[key].file) {
        formData.append(`creative.typeProperties[${key}]`, medias[key].file);
      }
    });
    const enableNativeBanner = _.get(
      this.creativeBasicFormData,
      'enableNativeBanner',
      false
    );
    formData.append(
      'creative.enableNativeBanner',
      enableNativeBanner.toString()
    );
    const isOldImageBanner =
      _.get(this.creativeBasicFormData, 'nativeBanner.300x250.imageUrl') !==
      undefined;
    const needGenH5 =
      enableNativeBanner && (this.type.includes('create') || isOldImageBanner);
    if (needGenH5) {
      Object.values(this.nativeToBannertemplates).forEach((template, index) => {
        template.templatePath &&
          formData.append(
            `creative.templates[${index}]`,
            template.templatePath
          );
      });
    }
    return formData;
  }
}

export class VideoSummaryModel extends DefaultCreativeSummaryModel {
  getMediaSummaryComponent(): any {
    return VideoMediaSummary;
  }

  getTypePropertiesSummary(): any {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    const skippableSetting = this.getSkippableSettingSummary(
      typeProperties.skippableSetting
    );
    const summary = {
      videoPlacement: this.getVideoPlacementSummary(
        typeProperties.videoPlacement
      )
    };
    if (!_.isEmpty(skippableSetting)) {
      summary['skippableSetting'] = skippableSetting;
    }
    const isThirdPartyVideo = typeProperties.videoSrc === '3rd';
    if (isThirdPartyVideo) {
      summary['adServingUrl'] = _.get(
        this.creativeBasicFormData,
        'medias.urlVideo.url'
      );
      summary['videoDuration'] = _.get(
        this.creativeBasicFormData,
        'medias.urlVideo.duration'
      );
    }
    return summary;
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    let summaryData = {
      isThirdPartyVideo:
        this.creativeBasicFormData.typeProperties.videoSrc === '3rd'
    };
    if (!_.isEmpty(medias)) {
      Object.keys(medias).forEach((key) => {
        const data = getMediaSummaryData(medias[key]);
        if (data) {
          summaryData[key] = data;
        }
      });
    }
    return summaryData;
  }

  getSkippableSettingSummary(skippableSettingData) {
    if (!skippableSettingData) {
      return;
    }

    if (!+skippableSettingData.skippable) {
      return i18n.t('videoForm.labels.nonSkippable');
    } else {
      return `${i18n.t('videoForm.labels.skippable')}:${i18n.t('creativeList.labels.skipOffset', { offset: skippableSettingData.skipOffset })}`;
    }
  }

  getVideoPlacementSummary(videoPlacement) {
    if (videoPlacement === undefined) {
      return;
    }
    switch (videoPlacement) {
      case VideoPlacement.IN_STREAM:
        return i18n.t('videoForm.labels.inStream');
      case VideoPlacement.OUT_STREAM:
        return i18n.t('videoForm.labels.outStream');
      default:
        return i18n.t('videoForm.labels.unlimitStream');
    }
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, ['creativeId', 'typeProperties', 'medias']);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    const typeProperties = _.omit(
      _.get(this.creativeBasicFormData, 'typeProperties', {}),
      ['videoSrc', 'skippableSetting']
    );
    Object.keys(typeProperties).forEach((key) => {
      formData.append(`creative.typeProperties[${key}]`, typeProperties[key]);
    });
    const skippable = !!+_.get(
      this.creativeBasicFormData,
      'typeProperties.skippableSetting.skippable',
      1
    );
    formData.append('creative.typeProperties[skippable]', skippable.toString());
    formData.append(
      'creative.typeProperties[nonSkippable]',
      (!skippable).toString()
    );
    skippable &&
      formData.append(
        'creative.typeProperties[skipOffset]',
        _.get(
          this.creativeBasicFormData,
          'typeProperties.skippableSetting.skipOffset',
          0
        )
      );
    const videoSrc = _.get(
      this.creativeBasicFormData,
      'typeProperties.videoSrc'
    );
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    if (videoSrc === '3rd') {
      formData.append('creative.typeProperties[width]', '300');
      formData.append('creative.typeProperties[height]', '150');
      formData.append(
        'creative.typeProperties[duration]',
        medias.urlVideo.duration
      );
      formData.append(
        'creative.typeProperties[adServingUrl]',
        medias.urlVideo.url
      );
    } else {
      formData.append('creative.typeProperties[width]', medias.fileVideo.width);
      formData.append(
        'creative.typeProperties[height]',
        medias.fileVideo.height
      );
      formData.append(
        'creative.typeProperties[duration]',
        Math.floor(medias.fileVideo.duration).toString()
      );
      formData.append(
        'creative.typeProperties[videoUrl]',
        medias.fileVideo.url
      );
      if (medias.fileVideo.file) {
        const assetId = await this.cloudStorageManager.uploadVideoToCloud(
          medias.fileVideo.file
        );
        if (assetId) {
          formData.append('creative.typeProperties[videoAssetId]', assetId);
          const videoUrl = await this.creativeManager.publicVideoAssetId(
            assetId,
            this.creativeBasicFormData.creativeType
          );
          formData.set('creative.typeProperties[videoUrl]', videoUrl);
        }
      }
    }

    return formData;
  }
}

export class ImageSummaryModel extends DefaultCreativeSummaryModel {
  getMediaSummaryComponent(): any {
    return ImageMediaSummary;
  }

  getTypePropertiesSummary(): any {
    return {};
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    return {
      medias: this.creativeBasicFormData.medias
    };
  }

  async getBasicSubmitData(): Promise<any> {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imageData = _.get(medias, 'image', {});
    formData.append('creative.typeProperties[width]', imageData.width);
    formData.append('creative.typeProperties[height]', imageData.height);
    let imageUrl = imageData.url;
    if (imageData.file) {
      imageUrl = await this.cloudStorageManager.uploadFileToCloud(
        imageData.file
      );
    }
    if (imageUrl) {
      formData.append('creative.typeProperties[imageUrl]', imageUrl);
    }

    return formData;
  }
}
export class CreateImageMultipleSummaryModel extends ImageSummaryModel {
  getMediaSummaryComponent(): any {
    return ImageMultipleMediaSummary;
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    return {
      medias: this.creativeBasicFormData.medias
    };
  }

  async getBasicSubmitData() {
    return new FormData();
  }

  async getJsonSubmitData() {
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imagesData = _.get(medias, 'images', {});
    const imagesArray: { file: File; width: number; height: number }[] =
      Object.values(imagesData);
    const submitData: any[] = [];
    const sizeImageMap: {
      [key: string]: { file: File; width: number; height: number }[];
    } = imagesArray.reduce((acc, image) => {
      const sizeDes = `${image.width}x${image.height}`;
      if (sizeDes in acc) {
        acc[sizeDes].push(image);
      } else {
        acc[sizeDes] = [image];
      }
      return acc;
    }, {});
    for (const size in sizeImageMap) {
      const imagesArray = sizeImageMap[size];
      for (let i = 0; i < imagesArray.length; ++i) {
        const image = imagesArray[i];
        const imageUrl = await this.cloudStorageManager.uploadFileToCloud(
          image.file
        );
        let name = `${this.creativeBasicFormData.name}_${size}`;
        if (imagesArray.length > 1) {
          name = `${name}(${i + 1})`;
        }
        submitData.push({
          creative: {
            name,
            landingPageUrl: this.creativeBasicFormData.landingPageUrl,
            creativeType: this.creativeBasicFormData.creativeType,
            advertiserId: this.creativeBasicFormData.advertiserId,
            tenmaxCategory: this.creativeBasicFormData.tenmaxCategory,
            enableStartTime: this.creativeBasicFormData.enableStartTime,
            enableEndTime: this.creativeBasicFormData.enableEndTime,
            bannerUrl: this.creativeBasicFormData.bannerUrl,
            typeProperties: {
              width: image.width.toString(),
              height: image.height.toString(),
              imageUrl
            },
            adLogo: this.creativeBasicFormData.adLogo
              ? this.getAdLogoData(this.creativeBasicFormData.adLogo)
              : undefined
          }
        });
      }
    }

    return submitData;
  }
}

export class PPSSummaryModel extends DefaultCreativeSummaryModel {
  getMediaSummaryComponent(): any {
    return PPSMediaSummary;
  }

  getTypePropertiesSummary(): any {
    const { layoutId, subLayout, htmlSnippet } =
      this.creativeBasicFormData.typeProperties;
    return {
      htmlSnippet,
      layoutId: i18n.t(
        `ppsLayoutId.${layoutId.toLowerCase().replace(/-|\s/g, '_')}`
      ),
      subLayout
    };
  }

  getMediaSummary(): any {
    return {
      htmlSnippet: this.creativeBasicFormData.typeProperties.htmlSnippet
    };
  }

  override async getCreativeBasicSummaryData() {
    const omitProps = [
      'advertiserId',
      'medias',
      'typeProperties',
      'bannerExtra',
      'nativeBanner',
      'bannerImageUrl',
      'ppsLayoutId',
      'adLogo',
      'lineMessage',
      'tenMaxBeaconMessage',
      'subLayout'
    ];
    const basic = _.omitBy(
      _.omit(this.creativeBasicFormData, omitProps),
      _.isUndefined
    );
    return _.uniqBy(
      _.compact([
        ...this.transferObjectToSummaryData(basic),
        ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
      ]),
      'label'
    );
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias',
      'bannerImageUrl',
      'ppsLayoutId',
      'subLayout'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    const { htmlSnippet, layoutId, subLayout } =
      this.creativeBasicFormData.typeProperties;
    formData.append('creative.typeProperties[htmlSnippet]', htmlSnippet);
    formData.append('creative.ppsLayoutId', `pps_${layoutId}`);
    subLayout && formData.append('creative.subLayout', subLayout);
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imageData = _.get(medias, 'image', {});
    let bannerImageUrl = imageData.url;
    if (imageData.file) {
      bannerImageUrl = await this.cloudStorageManager.uploadFileToCloud(
        imageData.file
      );
    }
    if (bannerImageUrl) {
      formData.append(
        'creative.typeProperties[bannerImageUrl]',
        bannerImageUrl
      );
    }
    return formData;
  }
}
export class HTML5SummaryModel extends DefaultCreativeSummaryModel {
  getMediaSummaryComponent(): any {
    return undefined;
  }

  getTypePropertiesSummary(): any {
    return {
      bannerSize: this.creativeBasicFormData.typeProperties.bannerSize
    };
  }

  getMediaSummary(): any {
    return {};
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias',
      'bannerImageUrl'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    const widthHeight = _.get(
      this.creativeBasicFormData.typeProperties,
      'bannerSize',
      '300x250'
    ).split('x');
    formData.append('creative.typeProperties[width]', widthHeight[0]);
    formData.append('creative.typeProperties[height]', widthHeight[1]);
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const file = _.get(medias, 'htmlZip.file');
    if (file) {
      formData.append('creative.typeProperties[h5File]', file);
    }
    return formData;
  }
}

abstract class SupportBeaconMessageCreativeSummaryModel extends DefaultCreativeSummaryModel {
  override async getCreativeBasicSummaryData() {
    const omitProps = [
      'advertiserId',
      'medias',
      'typeProperties',
      'bannerExtra',
      'nativeBanner',
      'bannerImageUrl',
      'ppsLayoutId',
      'adLogo',
      'lineMessage',
      'tenMaxBeaconMessage',
      'enableNativeBanner'
    ];
    const basic = _.omitBy(
      _.omit(this.creativeBasicFormData, omitProps),
      _.isUndefined
    );
    return _.uniqBy(
      _.compact([
        ...this.transferObjectToSummaryData(basic),
        ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
      ]),
      'label'
    );
  }

  override getBeaconSummary() {
    const beaconConnection = _.get(
      this.creativeBasicFormData,
      'typeProperties.beaconConnection'
    );
    if (!beaconConnection) {
      return undefined;
    }
    const maxFrequency = _.get(
      this.creativeBasicFormData,
      'typeProperties.maxFrequency'
    );
    const intervalHours = _.get(
      this.creativeBasicFormData,
      'typeProperties.intervalHours'
    );
    const language = _.get(
      this.creativeBasicFormData,
      'typeProperties.language'
    );
    const lineMessage = this.creativeBasicFormData.lineMessage;
    const tenMaxBeaconMessage = this.creativeBasicFormData.tenMaxBeaconMessage;
    const timeUnitKey = +intervalHours > 1 ? 'hours' : 'hour';
    const cappingCountKey =
      +maxFrequency > 1 ? 'cappingCounts' : 'cappingCount';
    const hasFrequencyCap = intervalHours > 0 && maxFrequency > 0;
    return {
      frequencyCap: hasFrequencyCap
        ? `${
            i18n.t('beaconFields.labels.frequencyCap.every') || ''
          } ${intervalHours} ${i18n.t(`common.units.${timeUnitKey}`)} ${i18n.t(
            'beaconFields.labels.frequencyCap.send'
          )} ${maxFrequency} ${i18n.t(
            `beaconFields.labels.frequencyCap.${cappingCountKey}`
          )}`
        : undefined,
      // TODO: 如果未來平台語系都定義用 BCP 47 的格式，可以調整 wording，就不是只單純 for Line Beacon
      language: language
        ? i18n.t(
            `beaconFields.labels.lineUserLanguage.${_.snakeCase(language)}`
          )
        : undefined,
      beaconConnection: beaconConnection.label,
      lineMessage,
      tenMaxBeaconMessage
    };
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias',
      'bannerUrl',
      'landingPageUrl',
      'lineMessage',
      'tenMaxBeaconMessage'
    ]);
    const beaconConnection = _.get(
      this.creativeBasicFormData,
      'typeProperties.beaconConnection'
    );
    if (!beaconConnection) {
      return formData;
    }
    formData.append(
      'creative.typeProperties[beaconConnectionId]',
      beaconConnection.value
    );

    const maxFrequency = _.get(
      this.creativeBasicFormData,
      'typeProperties.maxFrequency'
    );
    const intervalHours = _.get(
      this.creativeBasicFormData,
      'typeProperties.intervalHours'
    );
    const language = _.get(
      this.creativeBasicFormData,
      'typeProperties.language'
    );
    formData.append('creative.typeProperties[maxFrequency]', maxFrequency);
    formData.append('creative.typeProperties[intervalHours]', intervalHours);
    if (language !== LineUserLanguage.ALL) {
      formData.append('creative.typeProperties[language]', language);
    }

    const tenMaxBeaconMessage = this.creativeBasicFormData.tenMaxBeaconMessage;
    if (tenMaxBeaconMessage) {
      formData.append(
        'creative.typeProperties[beaconType]',
        BeaconType.TENMAX_BEACON
      );
      formData.append(
        'creative.tenMaxBeaconMessage.type',
        tenMaxBeaconMessage.type
      );
      formData.append(
        'creative.tenMaxBeaconMessage.title',
        tenMaxBeaconMessage.title
      );
      formData.append(
        'creative.tenMaxBeaconMessage.message',
        tenMaxBeaconMessage.message
      );
      tenMaxBeaconMessage.dataIOS &&
        formData.append(
          'creative.tenMaxBeaconMessage.dataIOS',
          tenMaxBeaconMessage.dataIOS
        );
      tenMaxBeaconMessage.dataAndroid &&
        formData.append(
          'creative.tenMaxBeaconMessage.dataAndroid',
          tenMaxBeaconMessage.dataAndroid
        );
      if (
        tenMaxBeaconMessage.bundleIds &&
        tenMaxBeaconMessage.bundleIds.length > 0
      ) {
        for (let i = 0; i < tenMaxBeaconMessage.bundleIds.length; i++) {
          formData.append(
            `creative.tenMaxBeaconMessage.bundleIds[${i}]`,
            tenMaxBeaconMessage.bundleIds[i]
          );
        }
      }
      return formData;
    }

    const lineMessage = this.creativeBasicFormData.lineMessage;
    if (lineMessage && lineMessage.length > 0) {
      formData.append(
        'creative.typeProperties[beaconType]',
        BeaconType.LINE_SIMPLE_BEACON
      );
      for (let i = 0; i < lineMessage.length; i++) {
        const message = lineMessage[i];
        formData.append(`creative.lineMessage[${i}].order`, i.toString());
        formData.append(`creative.lineMessage[${i}].type`, message.type);
        if (message.type === LineMessageType.TEXT) {
          formData.append(
            `creative.lineMessage[${i}].message`,
            message.message
          );
        } else if (message.type === LineMessageType.IMAGE) {
          const imageFile = _.get(message, 'image.file');
          const imageUrl = _.get(message, 'image.url');
          if (imageFile) {
            const url =
              await this.cloudStorageManager.uploadFileToCloud(imageFile);
            formData.append(`creative.lineMessage[${i}].imageUrl`, url);
          } else if (imageUrl) {
            formData.append(`creative.lineMessage[${i}].imageUrl`, imageUrl);
          }
        } else if (message.type === LineMessageType.VIDEO) {
          const videoFile = _.get(message, 'video.file');
          const videoUrl = _.get(message, 'video.url');
          if (videoFile) {
            const assetId =
              await this.cloudStorageManager.uploadVideoToCloud(videoFile);
            if (assetId) {
              const videoUrl = await this.creativeManager.publicVideoAssetId(
                assetId,
                this.creativeBasicFormData.creativeType
              );
              formData.append(`creative.lineMessage[${i}].videoUrl`, videoUrl);
            }
          } else if (videoUrl) {
            formData.append(`creative.lineMessage[${i}].videoUrl`, videoUrl);
          }
          const previewImageFile = _.get(message, 'previewImage.file');
          const previewImageUrl = _.get(message, 'previewImage.url');
          if (previewImageFile) {
            const url =
              await this.cloudStorageManager.uploadFileToCloud(
                previewImageFile
              );
            formData.append(`creative.lineMessage[${i}].previewImageUrl`, url);
          } else if (previewImageUrl) {
            formData.append(
              `creative.lineMessage[${i}].previewImageUrl`,
              previewImageUrl
            );
          }
        } else if (message.type === LineMessageType.JOIN) {
          formData.append(
            `creative.lineMessage[${i}].officialAccount`,
            message.officialAccount
          );
        } else if (message.type === LineMessageType.COUPON) {
          formData.append(
            `creative.lineMessage[${i}].couponId`,
            message.couponId
          );
        }
      }
      return formData;
    }

    return formData;
  }
}

export class OutdoorCreativeSummaryModel extends SupportBeaconMessageCreativeSummaryModel {
  // 純圖片（IMAGE）與「圖+音」共用這支：兩者的 medias 形狀相同，沒選音訊時 BE 會補靜音
  // mp3（ECOPD-5641）。宣告在 collectMediaDataStrategies 之前，class field 依宣告順序
  // 初始化，順序反了會在 map 裡拿到 undefined。
  private collectImageAudioMediaData = async (medias, formData) => {
    await this.collectImageData(medias, formData);
    await this.collectAudioData(medias, formData);
  };

  collectMediaDataStrategies = {
    [OutdoorType.VIDEO]: async (medias, formData) => {
      await this.collectVideoData(medias, formData);
    },
    [OutdoorType.VIDEO_IMAGE]: async (medias, formData) => {
      await this.collectVideoData(medias, formData);
      await this.collectImageData(medias, formData);
    },
    [OutdoorType.HTML5]: async (medias, formData) => {
      await this.collectHtmlData(medias, formData);
    },
    [OutdoorType.IMAGE_AUDIO]: this.collectImageAudioMediaData,
    [OutdoorType.IMAGE]: this.collectImageAudioMediaData
  };

  constructor(
    creativeBasicFormData,
    cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager);
  }

  getMediaSummaryComponent(): any {
    return OutdoorMediaSummary;
  }

  /**
   * 這份素材所屬的銷售渠道 id。
   *
   * 摘要 model 手上只有 creativeType，渠道 id 要繞 creativeTypeSettings 查回來
   * （同檔的 hasSelfCheckList 也是這樣拿 channelGroupId）。
   */
  protected get channelGroupId(): number | undefined {
    return ChannelGroupSetting.creativeTypeSettings().get(
      this.creativeBasicFormData.creativeType
    )?.channelGroupId;
  }

  getTypePropertiesSummary(): any {
    const creativeSize = _.get(
      this.creativeBasicFormData,
      'typeProperties.creativeSize'
    );
    const outdoorType = _.get(
      this.creativeBasicFormData,
      'typeProperties.outdoorType'
    );
    const duration = _.get(
      this.creativeBasicFormData,
      'typeProperties.duration'
    );
    return _.omitBy(
      {
        creativeSize,
        outdoorType: getOutdoorTypeLabel(outdoorType, this.channelGroupId),
        outdoorDuration:
          outdoorType === OutdoorType.HTML5
            ? `${duration} ${i18n.t('common.units.seconds')}`
            : i18n.t(`outdoorForm.labels.duration${duration}`)
      },
      _.isEmpty
    );
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    const useAudio = _.get(
      this.creativeBasicFormData,
      'typeProperties.useAudio',
      false
    );
    return {
      medias: {
        ...this.creativeBasicFormData.medias,
        audio: useAudio ? this.creativeBasicFormData.medias.audio : undefined
      }
    };
  }

  collectVideoData = async (medias, formData) => {
    const videoData = _.get(medias, 'video', {});
    if (videoData.file) {
      const assetId = await this.cloudStorageManager.uploadVideoToCloud(
        videoData.file
      );
      if (assetId) {
        formData.append('creative.typeProperties[videoAssetId]', assetId);
        formData.append(
          'creative.typeProperties[videoDuration]',
          Math.floor(videoData.duration).toString()
        );
      }
    }
  };

  collectImageData = async (medias, formData) => {
    const imageData = _.get(medias, 'image', {});
    if (imageData.file) {
      const url = await this.cloudStorageManager.uploadFileToCloud(
        imageData.file
      );
      formData.append('creative.typeProperties[imageUrl]', url);
    }
  };

  collectHtmlData = async (medias, formData) => {
    const htmlData = _.get(medias, 'html', {});
    if (htmlData.file) {
      const url = await this.cloudStorageManager.uploadFileToCloud(
        htmlData.file
      );
      formData.append('creative.typeProperties[htmlUrl]', url);
    }
  };

  collectAudioData = async (medias, formData) => {
    const audioData = _.get(medias, 'audio', {});
    if (audioData.file) {
      const audioUrl = await this.cloudStorageManager.uploadFileToCloud(
        audioData.file
      );
      formData.append('creative.typeProperties[audioUrl]', audioUrl);
      formData.append(
        'creative.typeProperties[audioDuration]',
        Math.floor(audioData.duration).toString()
      );
    }
    formData.append(
      'creative.typeProperties[useAudio]',
      _.get(this.creativeBasicFormData, 'typeProperties.useAudio', false)
    );
  };

  async getBasicSubmitData() {
    const formData = await super.getBasicSubmitData();
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    formData.append(
      'creative.typeProperties[outdoorType]',
      _.get(this.creativeBasicFormData, 'typeProperties.outdoorType')
    );
    formData.append(
      'creative.typeProperties[duration]',
      _.get(this.creativeBasicFormData, 'typeProperties.duration')
    );
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const outdoorType = _.get(
      this.creativeBasicFormData,
      'typeProperties.outdoorType'
    );
    const collectMediaDataStrategy =
      this.collectMediaDataStrategies[outdoorType];
    if (collectMediaDataStrategy) {
      await collectMediaDataStrategy(medias, formData);
    }

    const creativeSize: `${number} x ${number}` = _.get(
      this.creativeBasicFormData,
      'typeProperties.creativeSize',
      []
    );
    const [width, height] = creativeSize.split(' x ');
    formData.append('creative.typeProperties[width]', width);
    formData.append('creative.typeProperties[height]', height);
    return formData;
  }
}

export class DynamicOutdoorSummaryModel extends OutdoorCreativeSummaryModel {
  getMediaSummaryComponent(): any {
    return OutdoorMediaSummary;
  }

  private get usesEpaperDurationFormat(): boolean {
    return usesEpaperDurationFormat(
      this.channelGroupId,
      _.get(this.creativeBasicFormData, 'typeProperties.outdoorType')
    );
  }

  hasSelfCheckList = () => {
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const documentData = _.get(medias, 'selfCheckDocument', {});
    return documentData.file || documentData.url;
  };

  getTypePropertiesSummary(): any {
    const duration = _.get(
      this.creativeBasicFormData,
      'typeProperties.duration'
    );

    const summary = {
      ...super.getTypePropertiesSummary(),
      // 電子紙的時長動輒好幾分鐘，照該渠道的講法拆成分與秒顯示（360 秒講成「6 分鐘」）。
      outdoorDuration: this.usesEpaperDurationFormat
        ? formatEpaperDuration(+duration)
        : `${duration} ${i18n.t('common.units.seconds')}`
    };

    if (!this.hasSelfCheckList()) {
      return summary;
    }

    return _.omitBy(
      {
        ...summary,
        orderName: _.get(
          this.creativeBasicFormData,
          'typeProperties.orderName'
        ),
        orderBudget: `${Currency.NTD} ${_.get(this.creativeBasicFormData, 'typeProperties.orderBudget')}`,
        orderSchedule: `${_.get(this.creativeBasicFormData, 'typeProperties.orderStartDate')} ~ ${_.get(this.creativeBasicFormData, 'typeProperties.orderEndDate')}`
      },
      _.isEmpty
    );
  }

  async getBasicSubmitData() {
    const formData = await super.getBasicSubmitData();
    const outdoorType = _.get(
      this.creativeBasicFormData,
      'typeProperties.outdoorType'
    );
    if (outdoorType === OutdoorType.VIDEO) {
      const video = _.get(this.creativeBasicFormData, 'medias.video', {});
      if (video.width && video.height) {
        formData.delete('creative.typeProperties[width]');
        formData.delete('creative.typeProperties[height]');
        formData.append('creative.typeProperties[width]', video.width);
        formData.append('creative.typeProperties[height]', video.height);
      }
    }
    if (isImageAudioFamily(outdoorType)) {
      const image = _.get(this.creativeBasicFormData, 'medias.image', {});
      if (image.width && image.height) {
        formData.delete('creative.typeProperties[width]');
        formData.delete('creative.typeProperties[height]');
        formData.append('creative.typeProperties[width]', image.width);
        formData.append('creative.typeProperties[height]', image.height);
      }
    }

    const creativeTypeSetting = ChannelGroupSetting.creativeTypeSettings().get(
      this.creativeBasicFormData.creativeType
    );
    const localeMeta = LocaleMetaStore.getInstance().get();
    const isExpressPass =
      creativeTypeSetting && localeMeta
        ? localeMeta.property.addonProps.expressPass[
            creativeTypeSetting.channelGroupId
          ] !== undefined
        : false;

    if (outdoorType !== OutdoorType.VIDEO) {
      return formData;
    }

    if (!this.hasSelfCheckList() && !isExpressPass) {
      return formData;
    }

    formData.append(
      'creative.typeProperties[orderName]',
      _.get(this.creativeBasicFormData, 'typeProperties.orderName')
    );
    formData.append(
      'creative.typeProperties[orderBudget]',
      _.get(this.creativeBasicFormData, 'typeProperties.orderBudget')
    );
    formData.append(
      'creative.typeProperties[startDate]',
      _.get(this.creativeBasicFormData, 'typeProperties.orderStartDate')
    );
    formData.append(
      'creative.typeProperties[endDate]',
      _.get(this.creativeBasicFormData, 'typeProperties.orderEndDate')
    );

    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const documentData = _.get(medias, 'selfCheckDocument', {});
    if (this.hasSelfCheckList()) {
      if (documentData.file) {
        const fileUrl = await this.cloudStorageManager.uploadFileToCloud(
          documentData.file
        );
        formData.append('creative.typeProperties[selfCheckList]', fileUrl);
      } else {
        const fileUrl = _.get(documentData, 'url', '');
        formData.append('creative.typeProperties[selfCheckList]', fileUrl);
      }
    } else if (isExpressPass) {
      formData.append(
        'creative.typeProperties[selfCheckList]',
        OUTDOOR_DEFAULT_SELFCHECK_DOCUMENT
      );
    }
    return formData;
  }
}

export class OutdoorLineMessageSummaryModel extends OutdoorCreativeSummaryModel {
  override async getCreativeBasicSummaryData() {
    const omitProps = [
      'advertiserId',
      'medias',
      'typeProperties',
      'bannerExtra',
      'nativeBanner',
      'bannerImageUrl',
      'bannerUrl',
      'landingPageUrl',
      'ppsLayoutId',
      'adLogo',
      'lineMessage',
      'tenMaxBeaconMessage',
      'enableNativeBanner'
    ];
    const basic = _.omitBy(
      _.omit(this.creativeBasicFormData, omitProps),
      _.isUndefined
    );
    return _.uniqBy(
      _.compact([
        ...this.transferObjectToSummaryData(basic),
        ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
      ]),
      'label'
    );
  }

  override getMediaSummary(): any {
    return undefined;
  }

  override getTypePropertiesSummary(): any {
    const omitProps = ['creativeSize', 'outdoorType', 'outdoorDuration'];
    const summary = super.getTypePropertiesSummary();
    return _.omit(summary, omitProps);
  }
}

export class LineMessageSummaryModel extends OutdoorLineMessageSummaryModel {
  override async getBasicSubmitData() {
    const formData = await super.getBasicSubmitData();
    formData.delete('creative.typeProperties[width]');
    formData.delete('creative.typeProperties[height]');
    return formData;
  }
}

export class SandboxOutdoorSummaryModel extends OutdoorCreativeSummaryModel {
  getTypePropertiesSummary(): any {
    const summary = super.getTypePropertiesSummary();
    const duration = _.get(
      this.creativeBasicFormData,
      'typeProperties.duration'
    );
    const sandboxSummary = _.omitBy(
      {
        ...summary,
        outdoorDuration: `${duration} ${i18n.t('common.units.seconds')}`
      },
      _.isEmpty
    );
    return sandboxSummary;
  }
}

export class ComboSummaryModel extends DefaultCreativeSummaryModel {
  constructor(
    creativeBasicFormData,
    cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager);
  }

  getMediaSummaryComponent(): any {
    return ComboMediaSummary;
  }

  getTypePropertiesSummary(): any {
    return {};
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    return {
      medias: this.creativeBasicFormData.medias
    };
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const videoData = _.get(medias, 'video', {});
    if (videoData.file) {
      const assetId = await this.cloudStorageManager.uploadVideoToCloud(
        videoData.file
      );
      if (assetId) {
        formData.append('creative.typeProperties[videoAssetId]', assetId);
        formData.append(
          'creative.typeProperties[videoW]',
          videoData.width.toString()
        );
        formData.append(
          'creative.typeProperties[videoH]',
          videoData.height.toString()
        );
        formData.append(
          'creative.typeProperties[duration]',
          Math.floor(videoData.duration).toString()
        );
      }
    }

    const imageData = _.get(medias, 'image', {});
    if (imageData.file) {
      const url = await this.cloudStorageManager.uploadFileToCloud(
        imageData.file
      );
      formData.append('creative.typeProperties[imageUrl]', url);
    } else {
      formData.append('creative.typeProperties[imageUrl]', imageData.url);
    }

    return formData;
  }
}

export class CustomLayoutSummaryModel extends DefaultCreativeSummaryModel {
  constructor(
    creativeBasicFormData: CreativeFormBasicData,
    cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager(),
    creativeManager: CreativeManager = new DefaultCreativeManager(),
    private customLayoutManager: CustomLayoutManager = new DefaultCustomLayoutManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager, creativeManager);
  }

  getMediaSummaryComponent(): any {
    return CustomLayoutMediaSummary;
  }

  getTypePropertiesSummary(): any {
    const { customLayout, macros } = this.creativeBasicFormData.typeProperties;
    const macrosConfig = _.defaultTo(customLayout.macros, {});
    const macroSummary = _.mapKeys(macros, (value, key) => {
      const macroConfig = macrosConfig[key];
      return macroConfig.name;
    });
    return {
      customLayout: customLayout.name,
      ...macroSummary
    };
  }

  getMediaSummary(): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    return {
      medias: this.creativeBasicFormData.medias
    };
  }

  async getBasicSubmitData() {
    const formData = new FormData();
    this.setBasicFormData(formData, [
      'advertiserId',
      'creativeId',
      'typeProperties',
      'medias',
      'bannerImageUrl',
      'ppsLayoutId'
    ]);
    this.creativeBasicFormData['creativeId'] &&
      formData.append(
        'creative.id',
        this.creativeBasicFormData['creativeId'].toString()
      );
    formData.append(
      'creative.advertiserId',
      this.creativeBasicFormData['advertiserId']
        ? this.creativeBasicFormData['advertiserId'].toString()
        : '0'
    );
    const { customLayoutId, customLayout, macros } =
      this.creativeBasicFormData.typeProperties;
    formData.append('creative.ppsLayoutId', `pps_${customLayout.layoutId}`);
    formData.append('creative.typeProperties[customLayoutId]', customLayoutId);
    const mediaData = {
      ..._.get(this.creativeBasicFormData.medias, 'image', {}),
      ..._.get(this.creativeBasicFormData.medias, 'video', {})
    };
    const macrosMap: { [key: string]: string } = {};
    Object.keys(_.defaultTo(macros, {})).forEach((key) => {
      macrosMap[key] = macros[key];
    });
    const macroValueMap = await this.customLayoutManager.getMacroValueMap(
      mediaData,
      customLayout.macros,
      this.creativeBasicFormData.creativeType
    );
    Object.keys(macroValueMap).forEach((key) => {
      macrosMap[key] = macroValueMap[key];
    });
    formData.append(
      'creative.typeProperties[macros]',
      JSON.stringify(macrosMap)
    );
    return formData;
  }
}
